import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import FormularioButom from "./FormularioButom";
import FormularioInput from "./FormularioInput";
import FormularioList from "./FormularioList";
import Api from "../Services/Api";
import { useNavigate } from "react-router-dom";

const onlyDigits = (v = "") => String(v).replace(/\D/g, "");

const formatCpfCnpj = (value = "") => {
  const digits = onlyDigits(value);

  // CPF: 000.000.000-00
  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4")
      .slice(0, 14);
  }

  // CNPJ: 00.000.000/0000-00
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5")
    .slice(0, 18);
};

// valida CPF/CNPJ só pelo tamanho (11/14). Se quiser dígito verificador real, eu faço também.
const isValidCpfCnpj = (v = "") => {
  const digits = onlyDigits(v);
  return digits.length === 11 || digits.length === 14;
};

const From = (props) => {
  const [nome, setNome] = useState("");
  const [cpfcnpj, setCpfcnpj] = useState("");
  const [agencia, setAgencia] = useState("");
  const [conta, setConta] = useState("");
  const [pix, setPix] = useState("");
  const [listaBancos, setListaBancos] = useState("");
  const [listaConta, setListaConta] = useState("");
  const [loading, setLoading] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  const ListaBancos = [
    { label: "Banco do Brasil", value: "BANCO_DO_BRASIL" },
    { label: "Bradesco", value: "BRADESCO" },
    { label: "Caixa Econômica Federal", value: "CAIXA_ECONOMICA_FEDERAL" },
    { label: "Itaú", value: "ITAU" },
    { label: "Santander", value: "SANTANDER" },
    { label: "Nubank", value: "NUBANK" },
    { label: "Inter", value: "INTER" },
    { label: "C6 Bank", value: "C6_BANK" },
    { label: "Sicredi", value: "SICREDI" },
    { label: "Sicoob", value: "SICOOB" },
  ];

  const ListaContas = [
    { label: "Conta Corrente", value: "CORRENTE" },
    { label: "Conta Poupança", value: "POUPANCA" },
    { label: "Conta PJ", value: "PJ" },
  ];

  // ✅ Schema Yup
  const schema = useMemo(
    () =>
      yup.object({
        nome: yup.string().trim().min(3, "Informe o nome completo.").required("Nome é obrigatório."),
        cpfcnpj: yup
          .string()
          .required("CPF/CNPJ é obrigatório.")
          .test("cpfcnpj", "CPF/CNPJ inválido. Informe 11 (CPF) ou 14 (CNPJ) dígitos.", (v) => isValidCpfCnpj(v)),
        listaBancos: yup.string().required("Selecione o banco."),
        listaConta: yup.string().required("Selecione o tipo de conta."),
        agencia: yup.string().trim().min(3, "Agência inválida.").required("Agência é obrigatória."),
        conta: yup
          .string()
          .trim()
          .test("conta", "Conta inválida. Use 123456-7 (ou somente números).", (v) => {
            const s = String(v || "").trim();
            if (!s) return false;
            const [n, d] = s.split("-");
            const num = onlyDigits(n);
            const dig = d ? onlyDigits(d) : "";
            if (num.length < 4) return false;
            if (d && dig.length < 1) return false;
            return true;
          })
          .required("Conta é obrigatória."),
        pix: yup.string().trim().min(3, "Chave Pix inválida.").required("Chave Pix é obrigatória."),
      }),
    []
  );

  // ✅ 1) Carregar dados salvos ao abrir
  useEffect(() => {
    let mounted = true;

    Api.get("/bank-accounts/me")
      .then((res) => {
        if (!mounted) return;
        const d = res.data;
        if (!d) return;

        setNome(d.account_holder_name || "");
        setCpfcnpj(formatCpfCnpj(d.account_holder_cpf_cnpj || "")); // ✅ formata ao carregar
        setListaBancos(d.bank_name || "");
        setListaConta(d.account_type || "");
        setAgencia(d.branch || "");

        const numero = d.account_number || "";
        const digito = d.account_digit || "";
        setConta(numero && digito ? `${numero}-${digito}` : numero);

        setPix(d.pix_key || "");
      })
      .catch((err) => {
        console.error("Erro ao carregar conta:", err?.response?.data || err.message);
      })
      .finally(() => {
        if (!mounted) return;
        setCarregando(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const validateForm = async () => {
    try {
      setErrors({});
      await schema.validate(
        { nome, cpfcnpj, listaBancos, listaConta, agencia, conta, pix },
        { abortEarly: false }
      );
      return true;
    } catch (err) {
      const next = {};
      err.inner?.forEach((e) => {
        if (e.path) next[e.path] = e.message;
      });
      setErrors(next);
      return false;
    }
  };

  // ✅ 2) Salvar / Atualizar no banco
  const aoSalvar = async (evento) => {
    evento.preventDefault();

    const ok = await validateForm();
    if (!ok) return;

    const contaStr = String(conta).trim();
    const [account_number_raw, account_digit_raw] = contaStr.split("-");
    const account_number = onlyDigits(account_number_raw || "").trim();
    const account_digit = onlyDigits(account_digit_raw || "").trim() || null;

    const payload = {
      account_holder_name: nome.trim(),
      account_holder_cpf_cnpj: onlyDigits(cpfcnpj), // ✅ salva limpo no banco
      bank_name: listaBancos,
      account_type: listaConta,
      branch: String(agencia).trim(),
      account_number,
      account_digit,
      pix_key: String(pix).trim(),
    };

    try {
      setLoading(true);
      const { data } = await Api.post("/bank-accounts/me", payload);
      props.aContaCadastrada?.(data);
      navigate("/dashboard");
    } catch (err) {
      console.error("Erro ao salvar:", err?.response?.data || err.message);
      alert(err?.response?.data?.message || "Erro ao salvar dados bancários.");
    } finally {
      setLoading(false);
    }
  };

  if (carregando) {
    return <div style={{ padding: 12 }}>Carregando dados bancários...</div>;
  }

  return (
    <form id="bank-form" noValidate onSubmit={aoSalvar}>
      <FormularioInput
        type="text"
        size="full"
        title="Nome do titular da conta"
        required="required"
        id="nomeTitular"
        placeholder="Ex: João da Silva"
        helper={errors.nome || "Informe exatamente como está no cadastro do banco."}
        valor={nome}
        aoAlterado={(valor) => {
          setNome(valor);
          if (errors.nome) setErrors((p) => ({ ...p, nome: null }));
        }}
      />

      <FormularioInput
        size="full"
        type="text"
        title="CPF ou CNPJ do titular"
        required="required"
        id="cpfCnpj"
        placeholder="Ex: 000.000.000-00 ou 00.000.000/0000-00"
        helper={errors.cpfcnpj || "Informe um CPF ou CNPJ"}
        valor={cpfcnpj}
        aoAlterado={(valor) => {
          const formatted = formatCpfCnpj(valor);
          setCpfcnpj(formatted);
          if (errors.cpfcnpj) setErrors((p) => ({ ...p, cpfcnpj: null }));
        }}
      />

      <FormularioList
        itens={ListaBancos}
        title="Banco"
        required="required"
        id="banco"
        placeholder="Selecione"
        helper={errors.listaBancos || "Selecione na lista um banco."}
        lista="listaBancos"
        valor={listaBancos}
        aoAlterado={(valor) => {
          setListaBancos(valor);
          if (errors.listaBancos) setErrors((p) => ({ ...p, listaBancos: null }));
        }}
      />

      <FormularioList
        itens={ListaContas}
        title="Tipo de conta"
        required="required"
        id="contas"
        helper={errors.listaConta || "Selecione o tipo de conta."}
        lista="tipoConta"
        valor={listaConta}
        aoAlterado={(valor) => {
          setListaConta(valor);
          if (errors.listaConta) setErrors((p) => ({ ...p, listaConta: null }));
        }}
      />

      <FormularioInput
        type="text"
        title="Agência"
        required="required"
        id="agencia"
        placeholder="Ex: 0001-0"
        helper={errors.agencia || "Se houver dígito, inclua com traço. Ex: 0001-0"}
        valor={agencia}
        aoAlterado={(valor) => {
          setAgencia(valor);
          if (errors.agencia) setErrors((p) => ({ ...p, agencia: null }));
        }}
      />

      <FormularioInput
        type="text"
        title="Conta + dígito"
        required="required"
        id="conta"
        placeholder="Ex: 123456-7"
        helper={errors.conta || "Inclua o dígito verificador. Ex: 123456-7"}
        valor={conta}
        aoAlterado={(valor) => {
          setConta(valor);
          if (errors.conta) setErrors((p) => ({ ...p, conta: null }));
        }}
      />

      <FormularioInput
        type="text"
        size="full"
        title="Chave Pix"
        required="required"
        id="chavePix"
        placeholder="CPF, e-mail ou celular"
        helper={errors.pix || "Obrigatório para acelerar o pagamento dos saques."}
        valor={pix}
        aoAlterado={(valor) => {
          setPix(valor);
          if (errors.pix) setErrors((p) => ({ ...p, pix: null }));
        }}
      />

      <div className="form-footer">
        <div className="security-note">
          🔒 <strong>Segurança:</strong> seus dados bancários são utilizados apenas para transferências de comissão.
        </div>

        <FormularioButom icone="💾" disabled={loading}>
          {loading ? "Salvando..." : "Salvar dados bancários"}
        </FormularioButom>
      </div>
    </form>
  );
};

export default From;
